import React, { useEffect, useState, useContext, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { unauthenticatedRoutes, authenticatedRoutes } from './Routes'
import { signOut, fetchAuthSession } from "@aws-amplify/auth";
import Loader from "../components/loaders/Loader";
import Sidebar from "../components/layouts/Sidebar";
import Topbar from "../components/layouts/Topbar";
import GlobalContext from "../components/Context/GlobalContext";
import NotFound from "../components/Notfound/Notfound";
import { setPrivilegedArray } from "../store/slices/privilegeSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { RootState } from "../store";

const AppRoutes: React.FC = () => {
  const { isLoggedIn, setIsLoggedIn } = useContext(GlobalContext);
  const [isLoading, setIsLoading] = useState(true); // State to track loading
  const { privileges } = useSelector((state: RootState) => state.privileges)
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const checkAuth = async () => {
    try {
      const user = await fetchAuthSession({ forceRefresh: true });
      if (user.tokens) {
        // Safely parse privileges
        const privileges = user?.tokens?.idToken?.payload?.['custom:privileges'];
        if(!privileges){
          dispatch(setPrivilegedArray('superadmin'));
        }else{
          let privilegesArray: string[] = [];
          
          if (Array.isArray(privileges)) {
            privilegesArray = privileges as string[];
          } else if (typeof privileges === 'string') {
            privilegesArray = privileges.split(','); // Split by comma if privileges are a single string
          }
          dispatch(setPrivilegedArray(privilegesArray));
        }
        setIsLoggedIn(true);
        // Navigate to dashboard or default authenticated route
      } else {
        if (
          location.pathname !== "/forgot-password" &&
          location.pathname !== "/set-new-password" &&
          location.pathname !== "/password-changed"
        ) {
          setIsLoggedIn(false);
          navigate("/");
          localStorage.clear();
          signOut();
        }
      }

    } catch (error) {
      
      if (
        location.pathname !== "/forgot-password" &&
        location.pathname !== "/set-new-password" &&
        location.pathname !== "/password-changed"
      ) {
        setIsLoggedIn(false);
        navigate("/");
        localStorage.clear();
        signOut();
      }
    } finally {
      setTimeout(() => { //added the timeout to complete the loading animations
        setIsLoading(false); // Stop loading after 2 seconds
      }, 2000);
    }
  };

  function routeGuard(user, config = {}) {
    const {
      requiresAuth = false,
      allowedRoles = [],
      feature = null,
      allowSuspended = false
    } = config;
  
    // If auth required and user is not logged in
    if (requiresAuth && !user?.isAuthenticated) {
      return { allowed: false, redirect: '/login' };
    }
  
    // If user account is suspended and not allowed
    if (user?.status === 'suspended' && !allowSuspended) {
      return { allowed: false, redirect: '/suspended' };
    }
  
    // Role-based access
    if (allowedRoles.length > 0) {
      const hasAccess = allowedRoles.includes(user?.role);
      if (!hasAccess) {
        return { allowed: false, redirect: '/unauthorized' };
      }
    }
  
    // Feature flag check
    if (feature && !user?.features?.includes(feature)) {
      return { allowed: false, redirect: '/not-available' };
    }
  
    // Passed all checks
    return { allowed: true };
  }
  

  useEffect(() => {
    checkAuth(); // Call checkAuth on initial render
  }, []);

  // Show Loader while checking auth
  if (isLoading) {
    return <Loader />;
  }

  // Function to render routes dynamically with privilege checks
  const renderRoutes = (
    
    routes: typeof unauthenticatedRoutes | typeof authenticatedRoutes
  ) =>
    routes.map(({ path, element: Component, props, privilege }) => {
      // Check if the user has the required privilege
      if(privileges === 'superadmin') {
        return <Route key={path} path={path} element={<Component {...props} />} />;
      }
      if (privilege === 'allowed') {
        return <Route key={path} path={path} element={<Component {...props} />} />;
      }
      if (Array.isArray(privilege) && privilege.some(privilege => privileges.includes(privilege))) {
        return <Route key={path} path={path} element={<Component {...props} />} />;
      }
      if (privilege && !privileges.includes(privilege)) {
        return null; // Do not render the route if the user lacks the privilege
      }
      return <Route key={path} path={path} element={<Component {...props} />} />;
    });

  // Check if the current path matches any of the valid paths and verify privileges
  const showLayout = authenticatedRoutes.some((route) => {
    // Handle dynamic routes with regex
    const { path, privilege } = route;

    if (path.includes(":")) {
      const regexPath = path.replace(/:\w+/g, "[^/]+");
      const regex = new RegExp(`^${regexPath}$`);
      if (regex.test(location.pathname)) {
        // Check privilege for matching route
        return checkPrivilege(privilege);
      }
    } else if (path === location.pathname) {
      // Check privilege for matching route
      return checkPrivilege(privilege);
    }

    return false;
  });
  function checkPrivilege(privilege:any) {
    if(privileges === 'superadmin') {
      return true;
    }
    if (Array.isArray(privilege)) {
      return privilege.some((p) => privileges.includes(p));
    }
    return privilege === "allowed";
  }
  return (
    <div className="flex h-screen">
      {/* Show Sidebar and Topbar only when authenticated */}
      {!isLoggedIn ? (
        <Suspense fallback={<Loader />}>
          <Routes>
            {renderRoutes(unauthenticatedRoutes)}
            {/* <Route path="*" element={<NotFoundPage />} /> */}
          </Routes>
        </Suspense>
      ) : (
        <>
          {location.pathname !== "/not-found" ? (
            <>
              {showLayout && <Sidebar />}
              <div className="flex-1 flex flex-col w-mainwidth bg-white">
                {showLayout && <Topbar />}
                <div className="flex-1 bg-background_blue rounded-[8px] h-mainscreenheight scroll-auto">
                  <Suspense fallback={<Loader />}>
                    <Routes>
                      {renderRoutes(authenticatedRoutes)}
                      <Route path="/" element={<Navigate to="/" />} />
                    </Routes>
                  </Suspense>
                </div>
              </div>
            </>
          ) :
            <Suspense fallback={<Loader />}>
              <Routes>
                {/* Catch-all route */}
                <Route path="/not-found" element={<NotFound />} />
              </Routes>
            </Suspense>
          }
        </>
      )}
    </div>
  );
};

const App: React.FC = () => (
  <Router>
    <AppRoutes />
  </Router>
);

export default App;